import React, { useEffect, useMemo, useState } from 'react';
import { ZONES, BRAND } from '../data/constants';
import { useApp } from '../data/ColisContext';
import ModalPrompt from './ModalPrompt';
import { inputStyle, labelStyle, focusInput, blurInput } from '../utils/helpers';
import { apiUrl, parseApiResponse } from '../utils/api';

const INITIAL = {
  nom:         '',
  tel:         '',
  destinataire:'',
  adresse:     '',
  zone:        '',
  poids:       '',
};

export default function FormDepot({ onSuccess }) {
  const { addColis, setClientTel, user, showToast, createValise, colis, adminColis } = useApp();
  const [form, setForm]       = useState(INITIAL);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [doitPayer, setDoitPayer] = useState(false);
  const [montantDu, setMontantDu] = useState('');
  const [valiseCode, setValiseCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [waColisList, setWaColisList] = useState([]);
  const [waSelectedId, setWaSelectedId] = useState('');
  const [creatingBulk, setCreatingBulk] = useState(false);
  const [valiseModalOpen, setValiseModalOpen] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const isAgent = user?.agent === true || user?.agent === 1 || user?.agent === '1';
  const isAdmin = user?.is_admin === true || user?.is_admin === 1 || user?.is_admin === '1';

  const normalizePhone = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    let s = raw.replace(/[^0-9]/g, '');
    if (s.startsWith('00')) s = s.slice(2);
    return s;
  };

  useEffect(() => {
    if (!isAgent) return;
    const tel = normalizePhone(form.tel);
    if (!tel) return;

    const list = isAdmin ? adminColis : colis;
    const arr = Array.isArray(list) ? list : [];
    const found = arr.find(c => normalizePhone(c?.tel) === tel && String(c?.destinataire || '').trim());
    if (!found) return;

    setForm(f => {
      const current = String(f?.destinataire || '').trim();
      if (current) return f;
      return { ...f, destinataire: String(found?.destinataire || '') };
    });
  }, [adminColis, colis, form.tel, isAdmin, isAgent]);

  const valises = useMemo(() => {
    const list = isAdmin ? adminColis : colis;
    return (Array.isArray(list) ? list : [])
      .filter(c => {
        const id = String(c?.id || '').toUpperCase();
        if (id.startsWith('VAL-')) return true;
        const type = String(c?.meta?.type || '').toLowerCase();
        return type === 'valise';
      })
      .sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [adminColis, colis, isAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setClientTel(form.tel);

      const due = doitPayer ? Number(String(montantDu).replace(/\s+/g, '').replace(',', '.')) : 0;
      if (doitPayer && (!Number.isFinite(due) || due <= 0)) {
        throw new Error('Montant dû invalide');
      }

      let photoBase64 = '';
      const photos = Array.isArray(photoFiles) ? photoFiles : [];
      const photosBase64 = [];
      const photosNames = [];
      for (const file of photos) {
        if (!file) continue;
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(new Error('Impossible de lire la photo'));
          reader.onload = () => resolve(String(reader.result || ''));
          reader.readAsDataURL(file);
        });
        if (b64) {
          photosBase64.push(b64);
          photosNames.push(String(file?.name || ''));
        }
      }

      const valise = String(valiseCode || '').trim().toUpperCase();
      const parsedPoids = Number(String(form.poids || '').replace(/\s+/g, '').replace(',', '.'));
      const hasPoids = String(form.poids || '').trim() !== '';
      if (hasPoids && (!Number.isFinite(parsedPoids) || parsedPoids <= 0)) {
        throw new Error('Poids invalide');
      }

      const baseMeta = {
        ...(doitPayer ? { payment_required: true, amount_due: due } : {}),
        ...(hasPoids ? { poids: parsedPoids } : {}),
      };
      const meta = (isAgent || isAdmin) && valise ? { ...baseMeta, valise_id: valise } : (Object.keys(baseMeta).length ? baseMeta : undefined);

      await addColis({
        nom: form.nom,
        tel: form.tel,
        destinataire: form.destinataire,
        adresse: form.adresse,
        zone: form.zone,
        statut: isAgent ? 'entrepot_france' : 'depot',
        meta,
        photos_base64: photosBase64.length ? photosBase64 : undefined,
        photos_names: photosNames.length ? photosNames : undefined,
      });
      setForm(INITIAL);
      setPhotoFiles([]);
      setDoitPayer(false);
      setMontantDu('');
      setValiseCode('');
      if (onSuccess) onSuccess();
    } catch (err) {
      showToast(err?.message || 'Erreur lors de la création du colis', 'error');
    } finally {
      setLoading(false);
    }
  };

  const importFromWhatsApp = async () => {
    setImporting(true);
    try {
      const res = await fetch(apiUrl('whatsapp/latest'), {
        method: 'GET',
        credentials: 'include',
      });
      const out = await parseApiResponse(res);
      if (!out.ok) {
        const msg = out?.json?.message || out?.text || 'Erreur serveur';
        throw new Error(String(msg));
      }
      const data = out?.json || {};
      const list = Array.isArray(data?.colis_list) ? data.colis_list : (data?.colis ? [data.colis] : []);
      if (list.length === 0) throw new Error('Aucun colis WhatsApp trouvé');

      setWaColisList(list);
      const first = list[0];
      setWaSelectedId(first?.id || '');

      setForm(f => ({
        ...f,
        nom: first?.nom || '',
        destinataire: first?.destinataire || '',
        tel: first?.tel || '',
        adresse: first?.adresse || '',
        zone: first?.zone || '',
        poids: first?.poids != null ? String(first.poids) : '',
      }));
    } catch (err) {
      showToast(err?.message || "Impossible d'importer", 'error');
    } finally {
      setImporting(false);
    }
  };

  const applySelectedWhatsAppColis = () => {
    if (!waSelectedId) return;
    const selected = waColisList.find(c => c?.id === waSelectedId);
    if (!selected) return;
    setForm(f => ({
      ...f,
      nom: selected?.nom || '',
      destinataire: selected?.destinataire || '',
      tel: selected?.tel || '',
      adresse: selected?.adresse || '',
      zone: selected?.zone || '',
      poids: selected?.poids != null ? String(selected.poids) : '',
    }));
  };

  const createAllWhatsAppColis = async () => {
    if (!waColisList || waColisList.length === 0) return;
    setCreatingBulk(true);
    try {
      const importedRaw = localStorage.getItem('zaki_imported_wa_colis_ids');
      const imported = new Set(importedRaw ? JSON.parse(importedRaw) : []);

      let createdCount = 0;
      let skippedCount = 0;
      for (const c of waColisList) {
        const sourceId = c?.id || '';
        if (sourceId && imported.has(sourceId)) {
          skippedCount += 1;
          continue;
        }

        const parsedPoids = Number(String(c?.poids ?? '').replace(/\s+/g, '').replace(',', '.'));
        const hasPoids = c?.poids !== undefined && c?.poids !== null && String(c?.poids).trim() !== '' && Number.isFinite(parsedPoids) && parsedPoids > 0;

        const newColis = await addColis({
          nom: c?.nom || '',
          tel: c?.tel || '',
          destinataire: c?.destinataire || '',
          adresse: c?.adresse || '',
          zone: c?.zone || '',
          source: 'whatsapp',
          meta: {
            ...(sourceId ? { wa_source_id: sourceId } : {}),
            ...(hasPoids ? { poids: parsedPoids } : {}),
          },
        });

        if (sourceId) imported.add(sourceId);
        if (newColis?.tel) setClientTel(newColis.tel);
        createdCount += 1;
      }

      localStorage.setItem('zaki_imported_wa_colis_ids', JSON.stringify(Array.from(imported)));
      setWaColisList([]);
      setWaSelectedId('');
      setForm(INITIAL);
      showToast(`Créés: ${createdCount}${skippedCount ? ` (déjà importés: ${skippedCount})` : ''}`, 'success');
      if (onSuccess) onSuccess();
    } catch (err) {
      showToast(err?.message || 'Impossible de créer les colis', 'error');
    } finally {
      setCreatingBulk(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {(user?.is_admin || user?.agent) && (
        <div style={{
          background: '#F8FAFC',
          border: '1px solid #E2E8F0',
          borderRadius: 14,
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 900, color: '#0f172a', fontSize: 13 }}>Valise (optionnel)</div>
            <button
              type="button"
              disabled={loading}
              onClick={() => setValiseModalOpen(true)}
              style={{
                padding: '10px 12px',
                borderRadius: 12,
                border: 'none',
                background: loading ? '#e2e8f0' : '#0EA5E9',
                color: 'white',
                fontWeight: 900,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              🧳 Créer une valise
            </button>
          </div>

          <Field label="Choisir une valise" id="valise_code">
            <select
              id="valise_code"
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={valiseCode}
              onChange={(e) => setValiseCode(e.target.value)}
              onFocus={focusInput}
              onBlur={blurInput}
            >
              <option value="">Aucune</option>
              {valises.map(v => (
                <option key={v.id} value={String(v.id).toUpperCase()}>
                  {v.id} {v.nom ? `— ${v.nom}` : ''}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      <ModalPrompt
        open={valiseModalOpen}
        title="🧳 Créer une valise"
        description="Saisissez un nom pour la valise."
        placeholder="Ex: Valise Dakar"
        confirmLabel="Créer"
        onClose={() => setValiseModalOpen(false)}
        onConfirm={async (nom) => {
          const n = String(nom || '').trim();
          if (!n) {
            showToast('Nom requis', 'error');
            return;
          }
          try {
            const v = await createValise(n);
            const id = String(v?.id || '').trim().toUpperCase();
            if (id) setValiseCode(id);
            setValiseModalOpen(false);
          } catch (err) {
            showToast(err?.message || 'Erreur création valise', 'error');
          }
        }}
      />

      <button type="button" onClick={importFromWhatsApp} disabled={importing || loading} style={{
        padding:      '12px 14px',
        background:   importing ? '#e2e8f0' : '#0EA5E9',
        color:        'white',
        border:       'none',
        borderRadius: 14,
        fontWeight:   800,
        fontSize:     14,
        cursor:       importing || loading ? 'not-allowed' : 'pointer',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'center',
        gap:          10,
        transition:   'all 0.2s',
        boxShadow:    importing ? 'none' : '0 4px 16px rgba(14,165,233,0.35)',
        fontFamily:   'inherit',
      }}>
        {importing ? 'Import...' : 'Importer depuis WhatsApp'}
      </button>

      {waColisList.length > 0 && (
        <button type="button" onClick={createAllWhatsAppColis} disabled={creatingBulk || importing || loading} style={{
          padding:      '12px 14px',
          background:   creatingBulk ? '#e2e8f0' : '#16A34A',
          color:        'white',
          border:       'none',
          borderRadius: 14,
          fontWeight:   900,
          fontSize:     14,
          cursor:       creatingBulk || importing || loading ? 'not-allowed' : 'pointer',
          display:      'flex',
          alignItems:   'center',
          justifyContent: 'center',
          gap:          10,
          transition:   'all 0.2s',
          boxShadow:    creatingBulk ? 'none' : '0 4px 16px rgba(22,163,74,0.35)',
          fontFamily:   'inherit',
        }}>
          {creatingBulk ? 'Création...' : `Créer tous les colis WhatsApp (${waColisList.length})`}
        </button>
      )}

      {waColisList.length > 1 && (
        <div style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          background: '#F1F5F9',
          borderRadius: 14,
          padding: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>
            Choisir un colis WhatsApp ({waColisList.length})
          </div>

          <select
            value={waSelectedId}
            onChange={(e) => setWaSelectedId(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer', maxWidth: 420 }}
          >
            {waColisList.map(c => (
              <option key={c.id} value={c.id}>
                {c.nom || 'Colis'} — {c.destinataire || ''} — {c.tel || ''}
              </option>
            ))}
          </select>

          <button type="button" onClick={applySelectedWhatsAppColis} disabled={loading || importing} style={{
            padding: '10px 12px',
            borderRadius: 12,
            border: 'none',
            background: '#E0F2FE',
            color: '#0369A1',
            fontWeight: 900,
            cursor: loading || importing ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}>
            Remplir
          </button>
        </div>
      )}

      <Field label="Nom du colis *" id="nom">
        <input id="nom" required style={inputStyle} placeholder="Ex: Chaussures, documents..."
          value={form.nom} onChange={set('nom')} onFocus={focusInput} onBlur={blurInput} />
      </Field>

      <Row>
        <Field label="Destinataire *" id="destinataire">
          <input id="destinataire" required style={inputStyle} placeholder="Nom du destinataire"
            value={form.destinataire} onChange={set('destinataire')} onFocus={focusInput} onBlur={blurInput} />
        </Field>
        <Field label="Téléphone *" id="tel">
          <input id="tel" required style={inputStyle} placeholder="77 xxx xx xx"
            value={form.tel} onChange={set('tel')} onFocus={focusInput} onBlur={blurInput} />
        </Field>
      </Row>

      <div style={{
        background: '#F8FAFC',
        border: '1px solid #E2E8F0',
        borderRadius: 14,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 800, color: '#0f172a', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={doitPayer}
            onChange={(e) => setDoitPayer(e.target.checked)}
          />
          Doit payer
        </label>

        {doitPayer && (
          <Field label="Montant dû *" id="montant_du">
            <input
              id="montant_du"
              required
              inputMode="decimal"
              style={inputStyle}
              placeholder="Ex: 5000"
              value={montantDu}
              onChange={(e) => setMontantDu(e.target.value)}
              onFocus={focusInput}
              onBlur={blurInput}
            />
          </Field>
        )}
      </div>

      <Row>
        <Field label="Adresse livraison *" id="adresse">
          <input id="adresse" required style={inputStyle} placeholder="Ex: Villa 24, Rue 10..."
            value={form.adresse} onChange={set('adresse')} onFocus={focusInput} onBlur={blurInput} />
        </Field>
        <Field label="Zone" id="zone">
          <select id="zone" style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.zone} onChange={set('zone')} onFocus={focusInput} onBlur={blurInput}>
            <option value="">Choisir une zone...</option>
            {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>
      </Row>

      <Row>
        <Field label="Poids du colis (kg)" id="poids">
          <input
            id="poids"
            inputMode="decimal"
            style={inputStyle}
            placeholder="Ex: 2.5"
            value={form.poids}
            onChange={set('poids')}
            onFocus={focusInput}
            onBlur={blurInput}
          />
        </Field>
        <div />
      </Row>

      <Field label="Photo du produit" id="photo">
        <input
          id="photo"
          type="file"
          accept="image/*"
          multiple
          style={inputStyle}
          onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            setPhotoFiles(files);
          }}
          onFocus={focusInput}
          onBlur={blurInput}
        />
        {photoFiles.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#475569', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {photoFiles.map((f, idx) => (
              <div key={idx}>
                {f?.name || 'photo'} ({Math.round(((f?.size || 0) / 1024))} KB)
              </div>
            ))}
          </div>
        )}
      </Field>

      <button type="submit" disabled={loading} style={{
        padding:      '14px',
        background:   loading ? '#c7d2fe' : BRAND.gradient,
        color:        'white',
        border:       'none',
        borderRadius: 14,
        fontWeight:   700,
        fontSize:     15,
        cursor:       loading ? 'not-allowed' : 'pointer',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'center',
        gap:          10,
        transition:   'all 0.2s',
        marginTop:    4,
        boxShadow:    loading ? 'none' : '0 4px 16px rgba(79,70,229,0.35)',
        fontFamily:   'inherit',
      }}>
        {loading
          ? <><Spinner /> Enregistrement...</>
          : <>📦 Enregistrer le colis</>
        }
      </button>
    </form>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
      {children}
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      width: 16, height: 16,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'white',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 0.6s linear infinite',
    }} />
  );
}
